import { defineStore } from 'pinia';
import { Config } from '@/config';
import apiService from '@/services/api.service';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';

import type { ProjectModuleNewsItemConfigDto } from '@/services/types/api.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import { Constants } from '@/constants';
import { useI18n } from 'vue-i18n';
import router from '@/router';
import { buildTransportPayload, normalizeEmptyImageAltText } from '@/helpers/store.helper';

/**
 * Finds an news item by its ID in the given array.
 * @param {ProjectModuleNewsItemConfigDto[]} news - The array of news items.
 * @param {number} newsId - The ID of the news to find.
 * @returns {ProjectModuleNewsItemConfigDto | undefined} The found news or undefined.
 */
const findNews = (news: ProjectModuleNewsItemConfigDto[], newsId: number) => {
  return news.find((x) => x.id === newsId);
};

const buildNewsItemPayload = (newsItem: ProjectModuleNewsItemConfigDto) => {
  return buildTransportPayload(newsItem, {
    stripKeys: ['ProjectModule', 'created_at', 'updated_at', 'deleted_at'],
    normalize: [(payload) => normalizeEmptyImageAltText(payload.config.image)],
  });
};

/**
 * Returns the list of available status options with translated labels.
 * @returns {{ label: string; value: number }[]} Array of status options.
 */
function getStatuses(): { label: string; value: number }[] {
  const { t } = useI18n();
  return [
    { label: t('project-module.news.status.options.draft'), value: Constants.ProjectModule.NewsItem.Status.draft },
    {
      label: t('project-module.news.status.options.published'),
      value: Constants.ProjectModule.NewsItem.Status.published,
    },
  ];
}

export interface NewsListItemsState {
  newsListItems: ProjectModuleNewsItemConfigDto[];
  itemLoadingStates: Set<number>;
}

export const useNewsItemsStore = defineStore({
  id: 'newsItems',
  state: (): NewsListItemsState => ({
    newsListItems: [],
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    /**
     * Checks if a specific news item is currently loading.
     * @param {NewsListItemsState} state - The store state.
     * @returns {(id: number) => boolean} Function that returns true if the item is loading.
     */
    isItemLoading: (state) => (id: number) => state.itemLoadingStates.has(id),

    /**
     * Returns news items sorted by date (ascending).
     * @param {NewsListItemsState} state - The store state.
     * @returns {ProjectModuleNewsItemConfigDto[]} Sorted array of news.
     */
    orderedNewsListItems: (state) => {
      return [...state.newsListItems].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    },

    /**
     * Returns the list of status options.
     * @returns {{ label: string; value: number }[]} Status options.
     */
    getStatuses: () => getStatuses,

    /**
     * Finds an news item by its ID.
     * @param {NewsListItemsState} state - The store state.
     * @returns {(newsId: number) => ProjectModuleNewsItemConfigDto | undefined} Function that returns the news or undefined.
     */
    getNews: (state) => (newsId: number) => {
      return findNews(state.newsListItems, newsId);
    },

    /**
     * Retrieves a single news item directly by ID (alternative to getNews).
     * @param {NewsListItemsState} state - The store state.
     * @returns {(id: number) => ProjectModuleNewsItemConfigDto | undefined} Function that returns the news or undefined.
     */
    getItem:
      (state) =>
      (id: number): ProjectModuleNewsItemConfigDto | undefined =>
        state.newsListItems.find((e) => e.id === id),
  },
  actions: {
    /**
     * Fetches all news items for a given project module.
     * @param {number} projectModuleId - The ID of the project module.
     */
    async fetchNewsListItemsByModuleId(projectModuleId: number) {
      this.itemLoadingStates.add(projectModuleId);
      try {
        const { data } = await apiService.get<ProjectModuleNewsItemConfigDto[]>(
          `${Config.api.v2}/project-modules/${projectModuleId}/news-items`,
        );
        this.newsListItems = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(projectModuleId);
      }
    },

    /**
     * Fetches a single news item by its ID and updates/adds it to the store.
     * @param {number} id - The ID of the news item.
     */
    async fetchNews(id: number) {
      this.itemLoadingStates.add(id);
      try {
        const { data } = await apiService.get<ProjectModuleNewsItemConfigDto>(`${Config.api.v2}/news-items/${id}`);
        const index = this.newsListItems.findIndex((e) => e.id === id);
        if (index !== -1) {
          this.newsListItems[index] = data;
        } else {
          this.newsListItems.push(data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(id);
      }
    },

    /**
     * Creates a new empty news item object (not persisted yet).
     * @param {number} projectModuleId - The ID of the parent project module.
     * @returns {ProjectModuleNewsItemConfigDto} New news item with default values.
     */
    createNewsListItem(projectModuleId: number): ProjectModuleNewsItemConfigDto {
      return {
        project_module_id: projectModuleId,
        title: { en: '', fr: '' },
        description: { en: '', fr: '' },
        date: new Date().toISOString(),
        config: {
          image: null,
          content: { en: '', fr: '' },
        },
        status: Constants.ProjectModule.NewsItem.Status.draft,
      };
    },

    /**
     * Saves (creates or updates) an news item and refreshes the list.
     * On creation, navigates to the news details route.
     * @param {ProjectModuleNewsItemConfigDto} newsListItem - The news item to save.
     * @returns {Promise<boolean>} True if save was successful.
     */
    async saveNewsItem(newsListItem: ProjectModuleNewsItemConfigDto, project_id: number): Promise<boolean> {
      try {
        const payload = buildNewsItemPayload(newsListItem);
        let newsId = newsListItem.id;
        if (newsId) {
          await apiService.put<ProjectModuleNewsItemConfigDto>(`${Config.api.v2}/news-items/${newsId}`, payload);
        } else {
          const { data } = await apiService.post<ProjectModuleNewsItemConfigDto>(
            `${Config.api.v2}/news-items`,
            payload,
          );
          newsId = data.id;

          // Navigate to details after creation (adjust route name if needed)
          router.push({
            name: 'news_item_details',
            params: { id: newsId, projectId: project_id, moduleId: newsListItem.project_module_id },
          });
        }
        await this.fetchNews(newsId!);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },

    /**
     * Deletes an news item and refreshes the list.
     * @param {ProjectModuleNewsItemConfigDto} newsListItem - The news item to delete.
     */
    async deleteNewsListItem(newsListItem: ProjectModuleNewsItemConfigDto) {
      if (!newsListItem.id) return;
      try {
        await apiService.delete(`${Config.api.v2}/news-items/${newsListItem.id}`);
        await this.fetchNewsListItemsByModuleId(newsListItem.project_module_id);
      } catch (error) {
        console.error(error);
      }
    },

    /**
     * Bulk updates multiple news items (e.g., for reordering).
     * @param {...ProjectModuleNewsItemConfigDto[]} newsListItems - News items to update.
     */
    async updateNewsListItems(...newsListItems: ProjectModuleNewsItemConfigDto[]) {
      try {
        for (const newsListItem of newsListItems) {
          if (newsListItem.id) {
            await apiService.put(`${Config.api.v2}/news-items/${newsListItem.id}`, newsListItem);
          }
        }
        // Refresh the full list after bulk update
        if (newsListItems.length > 0) {
          await this.fetchNewsListItemsByModuleId(newsListItems[0].project_module_id);
        }
      } catch (error) {
        console.error(error);
      }
    },

    /**
     * Uploads a new image/banner for the news item.
     * @param {number} newsId - The ID of the news item.
     * @param {string} image - Data URL of the image.
     */
    async saveImage(newsId: number, options: SavingOptions) {
      try {
        await FileUploaderFactory.create(options.version).upload({
          files: options.files,
          metadata: options.metadata,
          url: `${Config.api.v2}/news-items/${newsId}/image`,
        });
        await this.fetchNews(newsId);

        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },

    /**
     * Removes the image/banner from the news item.
     * @param {number} newsId - The ID of the news item.
     */
    async removeImage(newsId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/news-items/${newsId}/image`);
        await this.fetchNews(newsId);
      } catch (error) {
        console.error(error);
      }
    },
  },
});
